import { Directive, ElementRef, HostBinding, HostListener, Input, OnDestroy, Renderer2 } from '@angular/core';

@Directive({
  selector: '[tooltip], [tooltipClass]',
  standalone: true
})
export class TooltipDirective {
  private tooltipEl: HTMLDivElement | null = null;
  private tooltipText = '';
  private readonly offset = 10;
  private readonly viewportPadding = 12;
  private readonly mobileBreakpoint = 768;

  constructor(private host: ElementRef<HTMLElement>, private renderer: Renderer2) {}

  @Input()
  set tooltip(value: string | null | undefined) {
    const next = value ?? '';
    this.tooltipText = next;
    this.titleAttr = '';
  }

  @Input() tooltipClass = '';

  @HostBinding('attr.title')
  titleAttr = '';

  @HostListener('mouseenter', ['$event'])
  @HostListener('focus', ['$event'])
  onShow(event: MouseEvent | FocusEvent): void {
    const text = this.formatTooltipText(this.tooltipText);
    if (!text) {
      return;
    }

    this.createTooltip(text);
    this.updatePosition(event);
  }

  @HostListener('mousemove', ['$event'])
  onMove(event: MouseEvent): void {
    this.updatePosition(event);
  }

  @HostListener('mouseleave')
  @HostListener('blur')
  onHide(): void {
    this.destroyTooltip();
  }

  ngOnDestroy(): void {
    this.destroyTooltip();
  }

  private createTooltip(text: string): void {
    if (this.tooltipEl) {
      this.tooltipEl.textContent = text;
      return;
    }

    const tooltip = this.renderer.createElement('div') as HTMLDivElement;
    tooltip.className = `wc-tooltip ${this.tooltipClass || ''}`.trim();
    tooltip.textContent = text;

    this.renderer.setStyle(tooltip, 'position', 'fixed');
    this.renderer.setStyle(tooltip, 'z-index', '10000');
    this.renderer.setStyle(tooltip, 'pointer-events', 'none');
    this.renderer.setStyle(tooltip, 'max-width', '300px');
    this.renderer.setStyle(tooltip, 'padding', '6px 8px');
    this.renderer.setStyle(tooltip, 'border-radius', '4px');
    this.renderer.setStyle(tooltip, 'background', 'rgba(15, 23, 42, 0.95)');
    this.renderer.setStyle(tooltip, 'color', '#fff');
    this.renderer.setStyle(tooltip, 'font-size', '12px');
    this.renderer.setStyle(tooltip, 'line-height', '1.35');
    this.renderer.setStyle(tooltip, 'white-space', 'normal');
    this.renderer.setStyle(tooltip, 'box-shadow', '0 6px 16px rgba(0, 0, 0, 0.25)');

    this.renderer.appendChild(document.body, tooltip);
    this.tooltipEl = tooltip;
  }

  private updatePosition(event: MouseEvent | FocusEvent): void {
    if (!this.tooltipEl) {
      return;
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const hostRect = this.host.nativeElement.getBoundingClientRect();
    const tooltipWidth = this.tooltipEl.offsetWidth;
    const tooltipHeight = this.tooltipEl.offsetHeight;
    const anchorX = event instanceof MouseEvent ? event.clientX : hostRect.left + hostRect.width / 2;
    const anchorY = event instanceof MouseEvent ? event.clientY : hostRect.top;

    let left = anchorX + this.offset;
    let top = anchorY + this.offset;

    if (viewportWidth <= this.mobileBreakpoint) {
      left = (viewportWidth - tooltipWidth) / 2;
      top = hostRect.bottom + this.offset;
    }

    const maxLeft = viewportWidth - tooltipWidth - this.viewportPadding;
    const maxTop = viewportHeight - tooltipHeight - this.viewportPadding;
    left = Math.max(this.viewportPadding, Math.min(left, maxLeft));
    top = Math.max(this.viewportPadding, Math.min(top, maxTop));

    this.renderer.setStyle(this.tooltipEl, 'left', `${Math.round(left)}px`);
    this.renderer.setStyle(this.tooltipEl, 'top', `${Math.round(top)}px`);
  }

  private destroyTooltip(): void {
    if (!this.tooltipEl) {
      return;
    }

    this.renderer.removeChild(document.body, this.tooltipEl);
    this.tooltipEl = null;
  }

  private formatTooltipText(value: string): string {
    return value
      .replace(/<br\s*\/?>(\n)?/gi, '\n')
      .replace(/<[^>]*>/g, ' ')
      .replace(/[ \t]+/g, ' ')
      .replace(/\n\s+/g, '\n')
      .trim();
  }

  private stripHtml(value: string): string {
    return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }
}
